/**
 * The types in this file are globally useful but are not actually part of the
 * service API.
 */

import { Nilable } from 'tsdef'
import { Amenity, Unit } from './fromSelfTour'
import { API_FloorPlate } from './self-tour.service.types'

/**
 * A map marker
 */
export interface Marker<ItemType extends Amenity | Unit> {
  building: Nilable<string>
  cssClasses: string
  floor: number
  icon?: string
  index: number
  label: string
  /**
   * The text for a unit's badge, including the building number when enabled.
   * Building maps use it as the pin label; tour maps label pins by stop.
   */
  badge?: string
  title: string
  type: 'AMENITY' | 'UNIT' | 'ENTRANCE'
  x: number
  y: number
  item: ItemType
}

/**
 * Type guard to check if a marker is a unit marker
 */
export function isUnitMarker(
  marker: Marker<Amenity | Unit>
): marker is Marker<Unit> {
  return marker.type === 'UNIT'
}

/**
 * Type guard to check if a marker is an amenity marker
 */
export function isAmenityMarker(
  marker: Marker<Amenity | Unit>
): marker is Marker<Amenity> {
  return marker.type === 'AMENITY' || marker.type === 'ENTRANCE'
}

export interface TourBuildings {
  amenities: Marker<Amenity>[]
  buildings: Building[]
  floors: Floor[]
  entrances: Marker<Amenity>[]
  markers: Marker<Amenity | Unit>[]
  ungrouped: {
    markers: Marker<Amenity | Unit>[]
    amenities: Marker<Amenity>[]
    units: Marker<Unit>[]
  }
  units: Marker<Unit>[]
}

export interface FloorGrouping extends Record<number | 'noFloor', Floor> {
  floors: Floor[]
}

export interface Building extends FloorGrouping {
  name: string
}

export interface Floor {
  floor: number | 'noFloor'
  floorPlate?: API_FloorPlate
  entrances: Marker<Amenity>[]
  markers: Marker<Amenity | Unit>[]
  units: Marker<Unit>[]
  amenities: Marker<Amenity>[]
}
